import { createFileRoute } from "@tanstack/react-router";

import voicemapIcon from "@/assets/voicemap-icon.png.asset.json";
import appStoreBadge from "@/assets/app-store-badge.png.asset.json";
import googlePlayBadge from "@/assets/google-play-badge.png.asset.json";

const URL = "https://www.travelbackland.com/mandatory-driving-instructions";

const VOICEMAP_IOS =
  "https://itunes.apple.com/us/app/voicemap/id852027939";
const VOICEMAP_ANDROID =
  "https://play.google.com/store/apps/details?id=me.voicemap.android";

export const Route = createFileRoute("/mandatory-driving-instructions")({
  head: () => ({
    meta: [
      { title: "Mandatory Driving Instructions — Backland" },
      {
        name: "description",
        content:
          "Required navigation instructions for guests driving to Backland. Download the VoiceMap audio tour before your trip.",
      },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Mandatory Driving Instructions — Backland" },
      { property: "og:url", content: URL },
    ],
    links: [{ rel: "canonical", href: URL }],
  }),
  component: MandatoryDrivingInstructionsPage,
});

function StepNumber({ n }: { n: number }) {
  return (
    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gold/95 text-background font-serif text-lg flex items-center justify-center">
      {n}
    </div>
  );
}

function Step({
  n,
  title,
  collapsible = false,
  children,
}: {
  n: number;
  title: React.ReactNode;
  collapsible?: boolean;
  children: React.ReactNode;
}) {
  const body = (
    <div className="text-foreground/80 leading-relaxed text-sm md:text-base space-y-3">
      {children}
    </div>
  );
  return (
    <li className="flex gap-5">
      <StepNumber n={n} />
      <div className="flex-1 pt-1">
        <h3 className="font-serif text-xl md:text-2xl mb-2 text-foreground">{title}</h3>
        {collapsible ? (
          <details className="group mt-2">
            <summary className="cursor-pointer list-none inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-gold/90 hover:text-gold transition select-none">
              <span className="group-open:hidden">Step details ▾</span>
              <span className="hidden group-open:inline">Hide details ▴</span>
            </summary>
            <div className="mt-4">{body}</div>
          </details>
        ) : (
          body
        )}
      </div>
    </li>
  );
}

function MandatoryDrivingInstructionsPage() {
  return (
    <div
      className="fixed inset-0 overflow-y-auto overscroll-contain no-scrollbar bg-background text-foreground"
      style={{ touchAction: "auto", overscrollBehavior: "auto" }}
    >
      

      {/* Hero */}
      <header className="relative w-full max-w-3xl mx-auto px-6 pt-14 md:pt-20 pb-10 text-center">
        <div className="text-[10px] tracking-[0.4em] uppercase text-gold/90 mb-4">
          For Registered Guests
        </div>
        <h1 className="font-serif text-3xl md:text-5xl leading-[1.1] text-foreground">
          Mandatory Instructions to Navigate to Backland
        </h1>
      </header>

      <main className="max-w-3xl mx-auto px-6 pb-20 space-y-14">
        {/* Intro / why */}
        <section className="space-y-5 text-foreground/85 leading-relaxed text-base md:text-lg">
          <p>
            Our secluded forest location is part of the magic of Backland, but it can also
            make it challenging to find. Google and Apple Maps give incorrect or inefficient
            directions and may take you on unsuitable roads.
          </p>
          <p>
            To avoid this and take the guesswork out of your journey, we have created a
            GPS-enabled audio tour that will guide you every step of the way into camp.
          </p>
          <div className="border-l-2 border-gold/80 pl-5 py-2 bg-gold/5 rounded-r">
            <p className="font-serif text-lg md:text-xl text-foreground">
              You must use this tour to navigate to camp. Do not use Google or Apple Maps.
            </p>
          </div>
          <p className="text-foreground/70 text-sm md:text-base">
            Follow the steps below to access and download the audio tour — it only takes
            about 5 minutes. <strong className="text-foreground">Do this before you leave
            for your trip, while you still have good internet access.</strong>
          </p>
        </section>

        {/* Before your trip */}
        <section>
          <div className="text-[10px] tracking-[0.4em] uppercase text-gold/85 mb-3">
            Part 1
          </div>
          <h2 className="font-serif text-2xl md:text-3xl mb-8 border-b border-border/60 pb-4">
            Before Your Trip
          </h2>
          <ol className="space-y-8">
            <Step n={1} title="Download VoiceMap">
              <div className="flex items-center gap-4 pt-1">
                <img
                  src={voicemapIcon.url}
                  alt="VoiceMap Audio Tours app icon"
                  className="w-16 h-16 rounded-2xl shadow-md flex-shrink-0"
                />
                <p className="text-foreground/80">
                  Get the free app from your phone's app store:
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 pt-3">
                <a
                  href={VOICEMAP_IOS}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Download on the App Store"
                  className="inline-block hover:opacity-85 transition"
                >
                  <img
                    src={appStoreBadge.url}
                    alt="Download on the App Store"
                    className="h-12 w-auto"
                  />
                </a>
                <a
                  href={VOICEMAP_ANDROID}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Get it on Google Play"
                  className="inline-block hover:opacity-85 transition"
                >
                  <img
                    src={googlePlayBadge.url}
                    alt="Get it on Google Play"
                    className="h-12 w-auto"
                  />
                </a>
              </div>
            </Step>

            <Step n={2} title="Sign up for a free account">
              <p>
                It's free — no credit card or trial required.
              </p>
            </Step>

            <Step 
              n={3} 
              title={
                <>
                  Enter the tour code<br />
                  {'\u00a0'}<span className="font-mono bg-foreground/10 px-2 py-0.5 rounded text-foreground">backland</span><br />
                  and download the tour
                </>
              } 
              collapsible
            >
              <p>
                Once signed in, tap <strong className="text-foreground">Tour Codes</strong>{" "}
                in the menu bar at the bottom of the screen. Then select{" "}
                <strong className="text-foreground">Enter Codes</strong> in the top right.
              </p>
              <p>
                Enter the code{" "}
                <span className="font-mono bg-foreground/10 px-2 py-0.5 rounded text-foreground">
                  backland
                </span>{" "}
                and hit the red <strong className="text-foreground">Submit</strong> button.
              </p>
              <p>
                A window will pop up saying{" "}
                <em>"Tour available to download. Going to Backland has been successfully
                added to your account."</em> Hit the red{" "}
                <strong className="text-foreground">Download Now</strong> button.
              </p>
              <p className="text-foreground/70">
                The tour is now in your library and you're ready for your trip.
              </p>
            </Step>
          </ol>
        </section>

        {/* When you begin */}
        <section>
          <div className="text-[10px] tracking-[0.4em] uppercase text-gold/85 mb-3">
            Part 2
          </div>
          <h2 className="font-serif text-2xl md:text-3xl mb-8 border-b border-border/60 pb-4">
            When You Begin the Drive to Backland
          </h2>
          <ol className="space-y-8">
            <Step n={4} title="Open VoiceMap and start the &quot;Going to Backland&quot; tour" collapsible>
              <p>
                Open the VoiceMap app, go to your library, and tap the{" "}
                <strong className="text-foreground">Going to Backland</strong> tour.
              </p>
              <p>
                Hit the red <strong className="text-foreground">Start Tour</strong> button,
                then tap <strong className="text-foreground">Get Directions</strong>. On
                the next screen, press the red{" "}
                <strong className="text-foreground">Get Directions</strong> button.
              </p>
            </Step>

            <Step n={5} title="Drive to the tour's starting point using your preferred map app" collapsible>
              <p>
                Google or Apple Maps will open and navigate you to the starting point of
                the audio tour. You may need to switch it from walking to{" "}
                <strong className="text-foreground">driving</strong> mode.
              </p>
              <p>
                At the end of the route, pull over at the{" "}
                <strong className="text-foreground">forest service information boards</strong>{" "}
                you'll see on your right, soon after leaving the paved road.
              </p>
            </Step>

            <Step n={6} title="Switch back to VoiceMap and click Start Tour. Let it guide you in." collapsible>
              <p>
                Once pulled over, manually switch back to the VoiceMap app and tap the red{" "}
                <strong className="text-foreground">Start Tour</strong> button.
              </p>
              <p>
                You should hear audio begin playing and see a map on your screen. Follow
                the instructions and it will guide you the rest of the way into camp.
              </p>
            </Step>
          </ol>
        </section>

        {/* Return tour */}
        <section className="border-t border-border/60 pt-10">
          <div className="text-[10px] tracking-[0.4em] uppercase text-gold/85 mb-3">
            On Your Way Out
          </div>
          <h2 className="font-serif text-xl md:text-2xl mb-3">
            A second tour to lead you back out
          </h2>
          <p className="text-foreground/80 leading-relaxed">
            We've created a second tour to guide you out of camp after your stay. To access
            it, repeat <strong className="text-foreground">Step 3</strong> above, but use
            the code{" "}
            <span className="font-mono bg-foreground/10 px-2 py-0.5 rounded text-foreground">
              glamping
            </span>{" "}
            instead.
          </p>
        </section>

        {/* Vehicle */}
        <section className="border-t border-border/60 pt-10">
          <div className="text-[10px] tracking-[0.4em] uppercase text-gold/85 mb-3">
            Good to Know
          </div>
          <h2 className="font-serif text-2xl md:text-3xl mb-5">
            Type of Vehicle to Bring
          </h2>
          <div className="space-y-4 text-foreground/80 leading-relaxed text-sm md:text-base">
            <p>
              The road to Backland is part of the adventure, but it may not be for everyone.
              The last <strong className="text-foreground">8 miles (around 15-20 minutes)</strong>{" "}
              of your trip will be on scenic, unpaved Forest Service roads.
            </p>
            <p>
              These roads are graveled, maintained, clearly marked, and are suitable for
              most standard vehicles. We do not suggest motorcycles or ultra-low-clearance
              sports cars.
            </p>
            <p>
              Four-wheel drive is generally not necessary, though it is{" "}
              <strong className="text-foreground">required in icy or snowy conditions</strong>.
              Expect some washboarding, potholes, and — depending on the weather — some
              shallow water.
            </p>
            <p>
              After your trip your car will likely be dusty (or muddy if it has rained). A
              trip to the car wash may be necessary if you've rented your vehicle. If you're
              anything like us, you'll find the destination is well worth the drive.
            </p>
          </div>
        </section>

        {/* Sign off */}
        <section className="border-t border-border/60 pt-10 text-center space-y-4">
          <p className="font-serif italic text-lg md:text-xl text-foreground/85">
            We look forward to hosting you at Backland.
          </p>
          <p className="text-foreground/70 text-sm md:text-base">
            For any questions or to add on to your stay, email us or call{" "}
            <a
              href="tel:+19282885441"
              className="text-gold hover:text-gold/80 underline underline-offset-4"
            >
              928-288-5441
            </a>
            .
          </p>
        </section>
      </main>
    </div>
  );
}